import type { Point, SketchPointerEvent } from "@/types/stage-types.js";
import type { SketchStrokeModel } from "../ToolManager.js";
import {
  pointsToPath,
  pointsToSmoothPath,
  pointsToPolylinePath,
} from "../StrokeRenderer.js";
import { createIconButton } from "@/components/UI/IconButton/IconButton.js";

export interface SelectionToolConfig {
  readonly root: SVGGElement;
  readonly getStrokes: () => readonly SketchStrokeModel[];
  readonly getSelectionMode?: () => "marquee" | "lasso";
  readonly onSelectionChange?: (
    selection: readonly SketchStrokeModel[]
  ) => void;
  readonly onTransformPreview?: (
    strokes: readonly SketchStrokeModel[]
  ) => void;
  readonly onTransformCommit?: (
    before: readonly SketchStrokeModel[],
    after: readonly SketchStrokeModel[],
    transform?: {
      readonly translate?: { readonly x: number; readonly y: number };
      readonly rotate?: {
        readonly angle: number;
        readonly origin?: { readonly x: number; readonly y: number };
      };
      readonly scale?: {
        readonly x: number;
        readonly y?: number;
        readonly origin?: { readonly x: number; readonly y: number };
      };
    }
  ) => void;
  readonly onTransformCancel?: (
    strokes: readonly SketchStrokeModel[]
  ) => void;
  readonly onDeleteSelection?: (
    selection: readonly SketchStrokeModel[]
  ) => void;
  readonly actionsHost?: HTMLElement | null;
  readonly getViewportRect?: () => DOMRect;
  readonly getWrapperRect?: () => DOMRect;
  readonly getLogicalSize?: () => { width: number; height: number };
  readonly getModifiers?: () => { readonly shift: boolean; readonly alt: boolean };
}

export interface SelectionToolAPI {
  readonly setActive: (active: boolean) => void;
  readonly handlePointerEvent: (event: SketchPointerEvent) => void;
  readonly clearSelection: () => void;
  readonly setSelection: (selection: readonly SketchStrokeModel[]) => void;
  readonly destroy: () => void;
}

const SVG_NS = "http://www.w3.org/2000/svg";
const HANDLE_SIZE = 12;
const ROTATE_HANDLE_RADIUS = 6;
const ROTATE_HANDLE_OFFSET = 28;
const TAP_THRESHOLD_SQ = 16;

const clonePoint = (point: Point): Point => ({ x: point.x, y: point.y });

const toPathData = (points: readonly Point[], closed: boolean): string => {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  let path = `M ${first!.x} ${first!.y}`;
  for (const point of rest) {
    path += ` L ${point.x} ${point.y}`;
  }
  if (closed && points.length > 2) {
    path += " Z";
  }
  return path;
};

const getBounds = (
  points: readonly Point[]
): { minX: number; minY: number; maxX: number; maxY: number } | null => {
  if (points.length === 0) return null;
  let minX = points[0]!.x;
  let maxX = points[0]!.x;
  let minY = points[0]!.y;
  let maxY = points[0]!.y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  return { minX, minY, maxX, maxY };
};

const boundsContainPoint = (
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  point: Point
): boolean =>
  point.x >= bounds.minX &&
  point.x <= bounds.maxX &&
  point.y >= bounds.minY &&
  point.y <= bounds.maxY;

const boundsIntersect = (
  a: { minX: number; minY: number; maxX: number; maxY: number },
  b: { minX: number; minY: number; maxX: number; maxY: number }
): boolean =>
  a.minX <= b.maxX &&
  a.maxX >= b.minX &&
  a.minY <= b.maxY &&
  a.maxY >= b.minY;

const pointInPolygon = (point: Point, polygon: readonly Point[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i]!.x;
    const yi = polygon[i]!.y;
    const xj = polygon[j]!.x;
    const yj = polygon[j]!.y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x <
        ((xj - xi) * (point.y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const getStrokeBounds = (
  stroke: SketchStrokeModel
): { minX: number; minY: number; maxX: number; maxY: number } | null =>
  getBounds(stroke.points);

const escapeId = (value: string): string =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(/[^a-zA-Z0-9_-]/g, "");

const strokeToolPriority = (stroke: SketchStrokeModel): number =>
  stroke.tool === "highlighter" ? 0 : 1;

const translatePoint = (point: Point, dx: number, dy: number): Point => ({
  x: point.x + dx,
  y: point.y + dy,
});

const scalePoint = (
  point: Point,
  anchor: Point,
  scaleX: number,
  scaleY: number
): Point => ({
  x: anchor.x + (point.x - anchor.x) * scaleX,
  y: anchor.y + (point.y - anchor.y) * scaleY,
});

const rotatePoint = (
  point: Point,
  center: Point,
  angle: number
): Point => {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
};

const buildUpdatedStroke = (
  stroke: SketchStrokeModel,
  points: readonly Point[]
): SketchStrokeModel => {
  const shouldClose =
    stroke.primitive === "rect" ||
    stroke.primitive === "square" ||
    stroke.primitive === "ellipse" ||
    stroke.primitive === "circle";
  const pathData =
    stroke.primitive
      ? pointsToPolylinePath(points, shouldClose)
      : stroke.pointerType === "pen"
        ? pointsToSmoothPath(points)
        : pointsToPath(points);
  return {
    ...stroke,
    points: points.map(clonePoint),
    pathData,
    updatedAt: performance.now?.() ?? Date.now(),
  };
};

const distanceSquared = (a: Point, b: Point): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

const distanceToSegmentSquared = (
  point: Point,
  start: Point,
  end: Point
): number => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return distanceSquared(point, start);
  }
  const t =
    ((point.x - start.x) * dx + (point.y - start.y) * dy) /
    (dx * dx + dy * dy);
  const clamped = Math.max(0, Math.min(1, t));
  const closest = {
    x: start.x + dx * clamped,
    y: start.y + dy * clamped,
  };
  return distanceSquared(point, closest);
};

export const createSelectionTool = (
  config: SelectionToolConfig
): SelectionToolAPI => {
  const overlayGroup = document.createElementNS(SVG_NS, "g");
  overlayGroup.classList.add("canvas-layer__sketch-selection");
  overlayGroup.setAttribute("pointer-events", "none");

  const lassoPath = document.createElementNS(SVG_NS, "path");
  lassoPath.classList.add("canvas-layer__sketch-selection-path");
  overlayGroup.appendChild(lassoPath);

  const boundsRect = document.createElementNS(SVG_NS, "rect");
  boundsRect.classList.add("canvas-layer__sketch-selection-bounds");
  overlayGroup.appendChild(boundsRect);

  const handles: SVGRectElement[] = [];
  for (let i = 0; i < 4; i += 1) {
    const handle = document.createElementNS(SVG_NS, "rect");
    handle.classList.add("canvas-layer__sketch-selection-handle");
    handles.push(handle);
    overlayGroup.appendChild(handle);
  }

  const rotateHandle = document.createElementNS(SVG_NS, "circle");
  rotateHandle.classList.add("canvas-layer__sketch-selection-rotate");
  overlayGroup.appendChild(rotateHandle);

  config.root.appendChild(overlayGroup);

  let actionsRoot: HTMLElement | null = null;
  let deleteButton: ReturnType<typeof createIconButton> | null = null;

  if (config.actionsHost) {
    actionsRoot = document.createElement("div");
    actionsRoot.className = "canvas-layer__sketch-selection-actions";
    actionsRoot.style.display = "none";
    actionsRoot.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
    });
    actionsRoot.addEventListener("pointerup", (event) => {
      event.stopPropagation();
    });
    actionsRoot.addEventListener("click", (event) => {
      event.stopPropagation();
    });
    deleteButton = createIconButton({
      icon: "erasers_clean",
      size: 28,
      iconSize: 24,
      transparentBg: false,
      className: "canvas-layer__sketch-selection-action",
      onClick: () => {
        if (!selectedStrokes.length) return;
        config.onDeleteSelection?.(selectedStrokes);
      },
    });
    actionsRoot.appendChild(deleteButton.getElement());
    config.actionsHost.appendChild(actionsRoot);
  }

  let isActive = false;
  let lassoPoints: Point[] = [];
  let selectionBounds:
    | { minX: number; minY: number; maxX: number; maxY: number }
    | null = null;
  let selectedIds = new Set<string>();
  let selectedStrokes: SketchStrokeModel[] = [];
  let lastModifiers = config.getModifiers?.() ?? { shift: false, alt: false };
  let activePointerId: number | null = null;
  let interactionMode: "lasso" | "marquee" | "move" | "scale" | "rotate" | null =
    null;
  let pendingMode: "lasso" | "marquee" | "move" | null = null;
  let tapStartPoint: Point | null = null;
  let activeScaleHandle: number | null = null;
  let transformStartPoint: Point | null = null;
  let transformAnchor: Point | null = null;
  let transformCenter: Point | null = null;
  let transformStartAngle = 0;
  let originalBounds:
    | { minX: number; minY: number; maxX: number; maxY: number }
    | null = null;
  let originalStrokes: SketchStrokeModel[] = [];

  const resolveSelectionMode = (): "marquee" | "lasso" =>
    config.getSelectionMode?.() ?? "lasso";

  const getRectPoints = (start: Point, end: Point): Point[] => {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    return [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
  };

  const setSelectionPath = (
    points: readonly Point[],
    closed: boolean
  ): void => {
    const pathData = toPathData(points, closed);
    if (!pathData) {
      lassoPath.removeAttribute("d");
      return;
    }
    lassoPath.setAttribute("d", pathData);
  };

  const clearBoundsVisuals = (): void => {
    boundsRect.setAttribute("display", "none");
    handles.forEach((handle) => handle.setAttribute("display", "none"));
    rotateHandle.setAttribute("display", "none");
  };

  const updateBoundsVisuals = (
    bounds: { minX: number; minY: number; maxX: number; maxY: number }
  ): void => {
    boundsRect.removeAttribute("display");
    boundsRect.setAttribute("x", `${bounds.minX}`);
    boundsRect.setAttribute("y", `${bounds.minY}`);
    boundsRect.setAttribute("width", `${Math.max(0, bounds.maxX - bounds.minX)}`);
    boundsRect.setAttribute("height", `${Math.max(0, bounds.maxY - bounds.minY)}`);

    const half = HANDLE_SIZE / 2;
    const positions: Point[] = [
      { x: bounds.minX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.minX, y: bounds.maxY },
    ];
    handles.forEach((handle, index) => {
      const pos = positions[index]!;
      handle.removeAttribute("display");
      handle.setAttribute("x", `${pos.x - half}`);
      handle.setAttribute("y", `${pos.y - half}`);
      handle.setAttribute("width", `${HANDLE_SIZE}`);
      handle.setAttribute("height", `${HANDLE_SIZE}`);
    });

    const centerX = (bounds.minX + bounds.maxX) / 2;
    rotateHandle.removeAttribute("display");
    rotateHandle.setAttribute("cx", `${centerX}`);
    rotateHandle.setAttribute("cy", `${bounds.minY - ROTATE_HANDLE_OFFSET}`);
    rotateHandle.setAttribute("r", `${ROTATE_HANDLE_RADIUS}`);
  };

  const updateActionsPosition = (
    bounds: { minX: number; minY: number; maxX: number; maxY: number } | null
  ): void => {
    if (
      !actionsRoot ||
      !bounds ||
      !config.getViewportRect ||
      !config.getLogicalSize
    ) {
      if (actionsRoot) {
        actionsRoot.style.display = "none";
      }
      return;
    }

    actionsRoot.style.display = "";
    actionsRoot.style.transform = "";
    const viewportRect = config.getViewportRect();
    const logical = config.getLogicalSize();
    const hostRect =
      (actionsRoot.offsetParent as HTMLElement | null)?.getBoundingClientRect() ??
      viewportRect;

    const anchor = {
      x: bounds.maxX,
      y: (bounds.minY + bounds.maxY) / 2,
    };
    const screenX =
      viewportRect.left + (anchor.x / logical.width) * viewportRect.width;
    const screenY =
      viewportRect.top + (anchor.y / logical.height) * viewportRect.height;
    const actionsRect = actionsRoot.getBoundingClientRect();
    const spacing = 12;
    let left = screenX - hostRect.left + spacing;
    const top = screenY - hostRect.top - actionsRect.height / 2;
    if (left + actionsRect.width > hostRect.width) {
      left = screenX - hostRect.left - spacing - actionsRect.width;
    }
    const clampedTop = Math.min(
      Math.max(8, top),
      hostRect.height - actionsRect.height - 8
    );
    actionsRoot.style.left = `${Math.round(left)}px`;
    actionsRoot.style.top = `${Math.round(clampedTop)}px`;
  };

  const updateStrokeSelection = (nextIds: Set<string>): void => {
    for (const id of selectedIds) {
      if (!nextIds.has(id)) {
        const element = config.root.querySelector(
          `[data-stroke-id="${escapeId(id)}"]`
        );
        element?.classList.remove("is-selected");
      }
    }
    for (const id of nextIds) {
      if (!selectedIds.has(id)) {
        const element = config.root.querySelector(
          `[data-stroke-id="${escapeId(id)}"]`
        );
        element?.classList.add("is-selected");
      }
    }
    selectedIds = nextIds;
  };

  const resolveModifiers = (
    event?: SketchPointerEvent
  ): { shift: boolean; alt: boolean } => {
    const fromUI = config.getModifiers?.() ?? { shift: false, alt: false };
    const original = event?.originalEvent;
    return {
      shift: Boolean(fromUI.shift || original?.shiftKey),
      alt: Boolean(fromUI.alt || original?.altKey),
    };
  };

  const findStrokeAtPoint = (point: Point): SketchStrokeModel | null => {
    const strokes = config.getStrokes();
    let candidate: SketchStrokeModel | null = null;
    let candidatePriority = -1;
    for (const stroke of strokes) {
      const bounds = getStrokeBounds(stroke);
      if (!bounds) continue;
      const padding = Math.max(6, stroke.width / 2 + 4);
      if (
        point.x < bounds.minX - padding ||
        point.x > bounds.maxX + padding ||
        point.y < bounds.minY - padding ||
        point.y > bounds.maxY + padding
      ) {
        continue;
      }
      const threshold = stroke.width / 2 + 6;
      const thresholdSq = threshold * threshold;
      let hit = false;
      const pts = stroke.points;
      if (pts.length === 1) {
        hit = distanceSquared(point, pts[0]!) <= thresholdSq;
      } else {
        for (let i = 0; i < pts.length - 1; i += 1) {
          const distSq = distanceToSegmentSquared(point, pts[i]!, pts[i + 1]!);
          if (distSq <= thresholdSq) {
            hit = true;
            break;
          }
        }
      }
      if (!hit) continue;
      const priority = strokeToolPriority(stroke);
      if (priority > candidatePriority) {
        candidate = stroke;
        candidatePriority = priority;
      } else if (priority === candidatePriority) {
        candidate = stroke;
      }
    }
    return candidate;
  };

  const shouldSelectOnly = (
    tapped: SketchStrokeModel,
    selection: readonly SketchStrokeModel[]
  ): boolean => {
    if (!selection.length) return true;
    if (selection.length === 1 && selection[0]!.id === tapped.id) {
      return false;
    }
    return true;
  };

  const handleTapSelection = (point: Point): void => {
    const hit = findStrokeAtPoint(point);
    if (!hit) {
      if (!lastModifiers.shift) {
        clearSelection();
      }
      return;
    }
    if (lastModifiers.shift) {
      if (selectedIds.has(hit.id)) {
        return;
      }
      const next = [...selectedStrokes, hit];
      setSelection(next);
      config.onSelectionChange?.(next);
      return;
    }
    if (shouldSelectOnly(hit, selectedStrokes)) {
      setSelection([hit]);
      config.onSelectionChange?.([hit]);
      return;
    }
    clearSelection();
  };

  const getScaleHandleAtPoint = (
    point: Point,
    bounds: { minX: number; minY: number; maxX: number; maxY: number }
  ): number | null => {
    const half = HANDLE_SIZE / 2;
    const positions: Point[] = [
      { x: bounds.minX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.minX, y: bounds.maxY },
    ];
    for (let index = 0; index < positions.length; index += 1) {
      const pos = positions[index]!;
      const minX = pos.x - half;
      const maxX = pos.x + half;
      const minY = pos.y - half;
      const maxY = pos.y + half;
      if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) {
        return index;
      }
    }
    return null;
  };

  const isRotateHandleHit = (
    point: Point,
    bounds: { minX: number; minY: number; maxX: number; maxY: number }
  ): boolean => {
    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = bounds.minY - ROTATE_HANDLE_OFFSET;
    const dx = point.x - centerX;
    const dy = point.y - centerY;
    return dx * dx + dy * dy <= ROTATE_HANDLE_RADIUS * ROTATE_HANDLE_RADIUS;
  };

  const updateSelection = (
    nextSelected: readonly SketchStrokeModel[]
  ): void => {
    const nextIds = new Set(nextSelected.map((stroke) => stroke.id));
    updateStrokeSelection(nextIds);
    selectedStrokes = nextSelected.map(cloneStroke);
    const selectionPoints = nextSelected.flatMap((stroke) => stroke.points);
    selectionBounds = getBounds(selectionPoints);
    if (selectionBounds) {
      updateBoundsVisuals(selectionBounds);
      updateActionsPosition(selectionBounds);
    } else {
      clearBoundsVisuals();
      updateActionsPosition(null);
    }
    config.onSelectionChange?.(nextSelected);
  };

  const applySelectionLasso = (points: readonly Point[]): void => {
    if (points.length < 3) {
      if (
        selectionBounds &&
        points.length > 0 &&
        boundsContainPoint(selectionBounds, points[0]!)
      ) {
        return;
      }
      clearSelection();
      return;
    }

    const polygonBounds = getBounds(points);
    if (!polygonBounds) {
      clearSelection();
      return;
    }

    const appendSelection = lastModifiers.shift;
    const nextSelected: SketchStrokeModel[] = appendSelection
      ? [...selectedStrokes]
      : [];
    const nextIds = new Set<string>(
      appendSelection ? selectedStrokes.map((stroke) => stroke.id) : []
    );
    for (const stroke of config.getStrokes()) {
      const strokeBounds = getStrokeBounds(stroke);
      if (!strokeBounds || !boundsIntersect(strokeBounds, polygonBounds)) {
        continue;
      }
      const hit = stroke.points.some((point) =>
        pointInPolygon(point, points)
      );
      if (hit) {
        if (!nextIds.has(stroke.id)) {
          nextSelected.push(stroke);
          nextIds.add(stroke.id);
        }
      }
    }

    updateSelection(nextSelected);
  };

  const applySelectionMarquee = (start: Point, end: Point): void => {
    const rectPoints = getRectPoints(start, end);
    const rectBounds = getBounds(rectPoints);
    if (!rectBounds) {
      clearSelection();
      return;
    }

    const appendSelection = lastModifiers.shift;
    const nextSelected: SketchStrokeModel[] = appendSelection
      ? [...selectedStrokes]
      : [];
    const nextIds = new Set<string>(
      appendSelection ? selectedStrokes.map((stroke) => stroke.id) : []
    );
    for (const stroke of config.getStrokes()) {
      if (
        stroke.points.length > 0 &&
        stroke.points.every((point) => boundsContainPoint(rectBounds, point))
      ) {
        if (!nextIds.has(stroke.id)) {
          nextSelected.push(stroke);
          nextIds.add(stroke.id);
        }
      }
    }

    updateSelection(nextSelected);
  };

  const clearSelection = (): void => {
    lassoPath.removeAttribute("d");
    updateStrokeSelection(new Set());
    selectionBounds = null;
    selectedStrokes = [];
    clearBoundsVisuals();
    updateActionsPosition(null);
    config.onSelectionChange?.([]);
  };

  const setSelection = (selection: readonly SketchStrokeModel[]): void => {
    const nextIds = new Set(selection.map((stroke) => stroke.id));
    updateStrokeSelection(nextIds);
    selectedStrokes = selection.map(cloneStroke);
    const points = selectedStrokes.flatMap((stroke) => stroke.points);
    const bounds = getBounds(points);
    selectionBounds = bounds;
    if (bounds) {
      updateBoundsVisuals(bounds);
      updateActionsPosition(bounds);
    } else {
      clearBoundsVisuals();
      updateActionsPosition(null);
    }
  };

  const cloneStroke = (stroke: SketchStrokeModel): SketchStrokeModel => ({
    ...stroke,
    points: stroke.points.map(clonePoint),
  });

  const resetTransformState = (): void => {
    interactionMode = null;
    pendingMode = null;
    activeScaleHandle = null;
    activePointerId = null;
    transformStartPoint = null;
    transformAnchor = null;
    transformCenter = null;
    transformStartAngle = 0;
    originalBounds = null;
    originalStrokes = [];
    tapStartPoint = null;
  };

  const getScaleAnchor = (
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    handleIndex: number
  ): Point => {
    switch (handleIndex) {
      case 0:
        return { x: bounds.maxX, y: bounds.maxY };
      case 1:
        return { x: bounds.minX, y: bounds.maxY };
      case 2:
        return { x: bounds.minX, y: bounds.minY };
      case 3:
        return { x: bounds.maxX, y: bounds.minY };
      default:
        return { x: bounds.maxX, y: bounds.maxY };
    }
  };

  const getScaleHandlePoint = (
    bounds: { minX: number; minY: number; maxX: number; maxY: number },
    handleIndex: number
  ): Point => {
    switch (handleIndex) {
      case 0:
        return { x: bounds.minX, y: bounds.minY };
      case 1:
        return { x: bounds.maxX, y: bounds.minY };
      case 2:
        return { x: bounds.maxX, y: bounds.maxY };
      case 3:
        return { x: bounds.minX, y: bounds.maxY };
      default:
        return { x: bounds.maxX, y: bounds.maxY };
    }
  };

  const beginTransform = (
    mode: "move" | "scale" | "rotate",
    event: SketchPointerEvent,
    handleIndex?: number | null
  ): void => {
    if (!selectionBounds || selectedStrokes.length === 0) return;
    interactionMode = mode;
    activePointerId = event.pointerId;
    transformStartPoint = clonePoint(event.position);
    originalBounds = { ...selectionBounds };
    originalStrokes = selectedStrokes.map(cloneStroke);
    if (mode === "scale" && typeof handleIndex === "number") {
      activeScaleHandle = handleIndex;
      transformAnchor = getScaleAnchor(selectionBounds, handleIndex);
    }
    if (mode === "rotate") {
      const center = {
        x: (selectionBounds.minX + selectionBounds.maxX) / 2,
        y: (selectionBounds.minY + selectionBounds.maxY) / 2,
      };
      transformCenter = center;
      transformStartAngle = Math.atan2(
        event.position.y - center.y,
        event.position.x - center.x
      );
    }
  };

  const buildTransformedStrokes = (
    event: SketchPointerEvent
  ): SketchStrokeModel[] => {
    if (!originalBounds || !transformStartPoint) return [];
    const modifiers = resolveModifiers(event);

    if (interactionMode === "move") {
      let dx = event.position.x - transformStartPoint.x;
      let dy = event.position.y - transformStartPoint.y;
      if (modifiers.shift) {
        if (Math.abs(dx) >= Math.abs(dy)) {
          dy = 0;
        } else {
          dx = 0;
        }
      }
      return originalStrokes.map((stroke) =>
        buildUpdatedStroke(
          stroke,
          stroke.points.map((point) => translatePoint(point, dx, dy))
        )
      );
    }

    if (interactionMode === "scale") {
      if (!transformAnchor || typeof activeScaleHandle !== "number") return [];
      const scaleAnchor = modifiers.alt
        ? {
            x: (originalBounds.minX + originalBounds.maxX) / 2,
            y: (originalBounds.minY + originalBounds.maxY) / 2,
          }
        : transformAnchor;
      const handlePoint = getScaleHandlePoint(
        originalBounds,
        activeScaleHandle
      );
      const originalVector = {
        x: handlePoint.x - scaleAnchor.x,
        y: handlePoint.y - scaleAnchor.y,
      };
      const nextVector = {
        x: event.position.x - scaleAnchor.x,
        y: event.position.y - scaleAnchor.y,
      };
      const rawScaleX =
        Math.abs(originalVector.x) < 0.001
          ? 1
          : nextVector.x / originalVector.x;
      const rawScaleY =
        Math.abs(originalVector.y) < 0.001
          ? 1
          : nextVector.y / originalVector.y;
      let scaleX = Math.max(0.05, Math.abs(rawScaleX));
      let scaleY = Math.max(0.05, Math.abs(rawScaleY));
      if (modifiers.shift) {
        const uniformScale = Math.max(scaleX, scaleY);
        scaleX = uniformScale;
        scaleY = uniformScale;
      }

      return originalStrokes.map((stroke) =>
        buildUpdatedStroke(
          stroke,
          stroke.points.map((point) =>
            scalePoint(point, scaleAnchor, scaleX, scaleY)
          )
        )
      );
    }

    if (interactionMode === "rotate") {
      if (!transformCenter) return [];
      const angle = Math.atan2(
        event.position.y - transformCenter.y,
        event.position.x - transformCenter.x
      );
      let delta = angle - transformStartAngle;
      if (modifiers.shift) {
        const snap = Math.PI / 4;
        delta = Math.round(delta / snap) * snap;
      }
      return originalStrokes.map((stroke) =>
        buildUpdatedStroke(
          stroke,
          stroke.points.map((point) =>
            rotatePoint(point, transformCenter!, delta)
          )
        )
      );
    }

    return [];
  };

  const applyTransformPreview = (strokes: SketchStrokeModel[]): void => {
    if (strokes.length === 0) return;
    config.onTransformPreview?.(strokes);
    selectedStrokes = strokes;
    const points = strokes.flatMap((stroke) => stroke.points);
    const bounds = getBounds(points);
    selectionBounds = bounds;
    if (bounds) {
      updateBoundsVisuals(bounds);
      updateActionsPosition(bounds);
    } else {
      clearBoundsVisuals();
      updateActionsPosition(null);
    }
  };

  const finishTransform = (event: SketchPointerEvent): void => {
    const updated = buildTransformedStrokes(event);
    const transformPayload = (() => {
      if (!originalBounds || !transformStartPoint) return null;
      const modifiers = resolveModifiers(event);
      if (interactionMode === "move") {
        let dx = event.position.x - transformStartPoint.x;
        let dy = event.position.y - transformStartPoint.y;
        if (modifiers.shift) {
          if (Math.abs(dx) >= Math.abs(dy)) {
            dy = 0;
          } else {
            dx = 0;
          }
        }
        if (Math.abs(dx) < 0.0001 && Math.abs(dy) < 0.0001) return null;
        return { translate: { x: dx, y: dy } };
      }

      if (interactionMode === "scale") {
        if (!transformAnchor || typeof activeScaleHandle !== "number") {
          return null;
        }
        const scaleAnchor = modifiers.alt
          ? {
              x: (originalBounds.minX + originalBounds.maxX) / 2,
              y: (originalBounds.minY + originalBounds.maxY) / 2,
            }
          : transformAnchor;
        const handlePoint = getScaleHandlePoint(
          originalBounds,
          activeScaleHandle
        );
        const originalVector = {
          x: handlePoint.x - scaleAnchor.x,
          y: handlePoint.y - scaleAnchor.y,
        };
        const nextVector = {
          x: event.position.x - scaleAnchor.x,
          y: event.position.y - scaleAnchor.y,
        };
        const rawScaleX =
          Math.abs(originalVector.x) < 0.001
            ? 1
            : nextVector.x / originalVector.x;
        const rawScaleY =
          Math.abs(originalVector.y) < 0.001
            ? 1
            : nextVector.y / originalVector.y;
        let scaleX = Math.max(0.05, Math.abs(rawScaleX));
        let scaleY = Math.max(0.05, Math.abs(rawScaleY));
        if (modifiers.shift) {
          const uniformScale = Math.max(scaleX, scaleY);
          scaleX = uniformScale;
          scaleY = uniformScale;
        }
        if (Math.abs(scaleX - 1) < 0.0001 && Math.abs(scaleY - 1) < 0.0001) {
          return null;
        }
        return {
          scale: {
            x: scaleX,
            y: scaleY,
            origin: scaleAnchor,
          },
        };
      }

      if (interactionMode === "rotate") {
        if (!transformCenter) return null;
        const angle = Math.atan2(
          event.position.y - transformCenter.y,
          event.position.x - transformCenter.x
        );
        let delta = angle - transformStartAngle;
        if (modifiers.shift) {
          const snap = Math.PI / 4;
          delta = Math.round(delta / snap) * snap;
        }
        if (Math.abs(delta) < 0.0001) return null;
        return {
          rotate: {
            angle: delta,
            origin: transformCenter,
          },
        };
      }
      return null;
    })();
    if (updated.length > 0 && originalStrokes.length > 0) {
      const originalById = new Map(
        originalStrokes.map((stroke) => [stroke.id, stroke])
      );
      const hasChanges = updated.some((stroke) => {
        const original = originalById.get(stroke.id);
        return !original || original.pathData !== stroke.pathData;
      });
      if (hasChanges) {
        config.onTransformCommit?.(
          originalStrokes,
          updated,
          transformPayload ?? undefined
        );
        selectedStrokes = updated;
        const points = updated.flatMap((stroke) => stroke.points);
        const bounds = getBounds(points);
        selectionBounds = bounds;
        if (bounds) {
          updateBoundsVisuals(bounds);
          updateActionsPosition(bounds);
        } else {
          clearBoundsVisuals();
          updateActionsPosition(null);
        }
      } else {
        config.onTransformCancel?.(originalStrokes);
        selectedStrokes = originalStrokes.map(cloneStroke);
        const points = selectedStrokes.flatMap((stroke) => stroke.points);
        const bounds = getBounds(points);
        selectionBounds = bounds;
        if (bounds) {
          updateBoundsVisuals(bounds);
          updateActionsPosition(bounds);
        } else {
          clearBoundsVisuals();
          updateActionsPosition(null);
        }
      }
    } else if (originalStrokes.length > 0) {
      config.onTransformCancel?.(originalStrokes);
    }
    resetTransformState();
  };

  const cancelTransform = (): void => {
    if (originalStrokes.length > 0) {
      config.onTransformCancel?.(originalStrokes);
      selectedStrokes = originalStrokes.map(cloneStroke);
      const points = selectedStrokes.flatMap((stroke) => stroke.points);
      const bounds = getBounds(points);
      selectionBounds = bounds;
      if (bounds) {
        updateBoundsVisuals(bounds);
        updateActionsPosition(bounds);
      } else {
        clearBoundsVisuals();
        updateActionsPosition(null);
      }
    }
    resetTransformState();
  };

  const handlePointerEvent = (event: SketchPointerEvent): void => {
    if (!isActive) return;
    lastModifiers = resolveModifiers(event);
    if (activePointerId !== null && event.pointerId !== activePointerId) {
      return;
    }

    switch (event.phase) {
      case "down": {
        activePointerId = event.pointerId;
        if (selectionBounds && selectedStrokes.length > 0) {
          if (isRotateHandleHit(event.position, selectionBounds)) {
            beginTransform("rotate", event);
            return;
          }
          const handleIndex = getScaleHandleAtPoint(
            event.position,
            selectionBounds
          );
          if (handleIndex !== null) {
            beginTransform("scale", event, handleIndex);
            return;
          }
          if (boundsContainPoint(selectionBounds, event.position)) {
            pendingMode = "move";
            tapStartPoint = clonePoint(event.position);
            return;
          }
        }
        pendingMode = resolveSelectionMode();
        tapStartPoint = clonePoint(event.position);
        break;
      }
      case "move": {
        if (
          pendingMode &&
          tapStartPoint &&
          distanceSquared(tapStartPoint, event.position) > TAP_THRESHOLD_SQ
        ) {
          if (pendingMode === "move") {
            beginTransform("move", {
              ...event,
              position: tapStartPoint,
            });
          } else if (pendingMode === "marquee") {
            interactionMode = "marquee";
            lassoPoints = [tapStartPoint, event.position];
            setSelectionPath(getRectPoints(tapStartPoint, event.position), true);
          } else {
            interactionMode = "lasso";
            lassoPoints = [tapStartPoint, event.position];
            setSelectionPath(lassoPoints, false);
          }
          pendingMode = null;
        }
        if (interactionMode === "lasso") {
          lassoPoints.push(event.position);
          setSelectionPath(lassoPoints, false);
          return;
        }
        if (interactionMode === "marquee") {
          lassoPoints[1] = event.position;
          setSelectionPath(getRectPoints(lassoPoints[0]!, event.position), true);
          return;
        }
        if (
          interactionMode === "move" ||
          interactionMode === "scale" ||
          interactionMode === "rotate"
        ) {
          const updated = buildTransformedStrokes(event);
          applyTransformPreview(updated);
        }
        break;
      }
      case "up": {
        if (interactionMode === "lasso") {
          lassoPoints.push(event.position);
          setSelectionPath(lassoPoints, true);
          applySelectionLasso(lassoPoints);
          lassoPath.removeAttribute("d");
          lassoPoints = [];
          interactionMode = null;
        } else if (interactionMode === "marquee") {
          const start = lassoPoints[0];
          if (start) {
            applySelectionMarquee(start, event.position);
          } else {
            clearSelection();
          }
          lassoPath.removeAttribute("d");
          lassoPoints = [];
          interactionMode = null;
        } else if (pendingMode) {
          handleTapSelection(event.position);
          pendingMode = null;
        } else if (
          interactionMode === "move" ||
          interactionMode === "scale" ||
          interactionMode === "rotate"
        ) {
          finishTransform(event);
        }
        activePointerId = null;
        tapStartPoint = null;
        break;
      }
      case "cancel": {
        if (interactionMode === "lasso") {
          lassoPath.removeAttribute("d");
          lassoPoints = [];
          interactionMode = null;
        } else if (interactionMode === "marquee") {
          lassoPath.removeAttribute("d");
          lassoPoints = [];
          interactionMode = null;
        } else if (pendingMode) {
          pendingMode = null;
          tapStartPoint = null;
        } else if (
          interactionMode === "move" ||
          interactionMode === "scale" ||
          interactionMode === "rotate"
        ) {
          cancelTransform();
        }
        activePointerId = null;
        break;
      }
      default:
        break;
    }
  };

  const setActive = (active: boolean): void => {
    isActive = active;
    if (!isActive) {
      resetTransformState();
      lassoPoints = [];
      lassoPath.removeAttribute("d");
      clearSelection();
    }
  };

  const destroy = (): void => {
    setActive(false);
    overlayGroup.remove();
    deleteButton?.destroy();
    deleteButton = null;
    if (actionsRoot && actionsRoot.parentElement) {
      actionsRoot.parentElement.removeChild(actionsRoot);
    }
    actionsRoot = null;
  };

  return {
    setActive,
    handlePointerEvent,
    clearSelection,
    setSelection,
    destroy,
  };
};
